import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import EdgeSentence from './EdgeSentence';
import RelationTree from './RelationTree';
import RelationField from './RelationField';
import TagField from './TagField';
import ObjectPicker from './ObjectPicker';
import { wvConfirm } from './Confirm';
import { usePins, togglePin, recordVisit } from './workingSet';
import CaptureClaim, { placeFromSelection } from './CaptureClaim';
import Markdown from './Markdown';
import ProseRelationChips from './ProseRelationChips';
import { LensChips, fmtScore, scoreColor } from './trustShared';
import TrustBandDrag from './TrustBandDrag';
import TrustStance from './TrustStance';
import { useLensWeights, recombineWithAxes } from './lensWeights';
import { Explain } from './Explain';
import './css/trust.css';

// The one object surface (UX session 2026-07-03: ObjectDetail + ClaimView merged).
// Disclosure ladder:
//   L0 — content, trust band (marker = your blend, width = lens spread), top
//        named-tension sentence, edges as votable sentences. Three-state
//        semantics: no_signal renders as ABSENCE, never as 0.
//   L1 — full lens chips + weights, add relation/tag, similar-but-unlinked,
//        same-source siblings, compare. (The grouped-edge list that lived here
//        was replaced by the L0 tree 2026-07-28; delete moved into it.)
//   L2 — machinery: creator, visibility, system edges, ids, engine pointers.

import { snip as snipAt } from './snip';
// The shared attribution renderer (2026-08-14). This page is a READING surface,
// so the object's own content renders in full (it always has, via Markdown);
// `snip` below stays for the index-grain lists further down — similar-but-
// unlinked, same-source siblings, the picker.
import Byline from './Byline';

const snip = (s, n = 110) => snipAt(s, n);

// Widen-only re-share, creator-only, in the Machinery fold beside the
// visibility facts it changes. POST /api/objects/:id/share only INSERTS
// visible_to edges (the widen-only ruling), so nothing here can narrow.
function ShareWider({ objectId }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiFetch('/api/groups')
      .then((r) => (r.ok ? r.json() : []))
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => {});
  }, [open]);

  const share = async (body, label) => {
    setBusy(true);
    setNote(null);
    try {
      const res = await apiFetch(`/api/objects/${objectId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'failed');
      setNote(json.widened ? `Now also readable by ${label}.` : json.note || 'Already readable there.');
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 'var(--wv-sp-5)' }}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={{ fontSize: 'var(--wv-fs-md)', background: 'none' }}>
          Share this wider…
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--wv-sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>Also share with</span>
          <button className="wv-chip" disabled={busy} onClick={() => share({ human: 'public' }, 'everyone')}>
            everyone
          </button>
          {groups.map((g) => (
            <button
              key={g.object_id}
              className="wv-chip"
              disabled={busy}
              onClick={() => share({ human: 'group', groups: [g.object_id] }, `the group “${g.name}”`)}
            >
              {g.name}
            </button>
          ))}
          <button className="wv-chip" disabled={busy} onClick={() => setOpen(false)}>close</button>
          {note && <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>{note}</span>}
        </div>
      )}
    </div>
  );
}

const TYPE_LABEL = { text: 'Object', claim: 'Claim', link: 'Link', tag: 'Tag', user: 'Person', group: 'Group' };

// The types that carry NO trust reading, with the one line each shows in place
// of a band (2026-08-08). Mirrors UNSCORED_OBJECT_TYPES in backend/trust/graph.ts
// and isUnscoredNode in walk.js, which is the authority.
//
// This is a MAP rather than a set because the reason differs per type and the
// reason is the whole content of the line. "Not scored" alone would leave a
// reader to guess whether the object is unscorable or merely unjudged — the two
// states this branch exists to keep apart.
const UNSCORED_KINDS = {
  question: { concept: 'question', line: 'Not scored — a question is settled, not asserted.' },
  thread: { concept: 'thread', line: 'Not scored — a thread arranges claims; the claims keep their own scores.' },
  heading: { concept: 'heading', line: 'Not scored — a heading names a part of a sequence. It asserts nothing.' },
  viewset: { concept: 'viewset', line: 'Not scored — a viewset selects claims; the claims keep their own scores.' },
  edge: { concept: 'edge-handle', line: 'Not scored — this stands for a connection so it can be argued about. The connection is activated by votes on the connection itself.' },
};

// NeighborRow is GONE (2026-07-28). It rendered the two lists the tree absorbed
// — "From the same source" and "Similar, not yet linked" — and every part of it
// found a home: the score and lens chips became the tree's per-row trust
// disclosure, the quick-link verbs became the "not yet connected" group, and
// `+ compare` moved into the disclosure panel. That last one is the reason to
// read this note: compare was the ONLY feeder for the compare bar at the bottom
// of this page, so deleting these cards without moving it would have left a
// shipped surface stranded with no way to reach it.

function Fold({ label, open, onToggle, children }) {
  return (
    <div style={{ marginTop: 'var(--wv-sp-8)' }}>
      <h2
        onClick={onToggle}
        style={{ cursor: 'pointer', userSelect: 'none', fontSize: 'var(--wv-fs-xl)', marginBottom: 'var(--wv-sp-4)' }}
      >
        <span className={`wv-chevron${open ? ' down' : ''}`} aria-hidden="true" /> {label}
      </h2>
      {open && children}
    </div>
  );
}

function ObjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { weights, axisWeights } = useLensWeights();
  const navigate = useNavigate();

  const [object, setObject] = useState(null);
  const [relations, setRelations] = useState([]);
  const [breakdown, setBreakdown] = useState(null); // null for user/group/tag objects
  const [allRelationTypes, setAllRelationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // disclosure ladder
  const [showDetails, setShowDetails] = useState(false);
  const [showMachinery, setShowMachinery] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // edit / extract
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const contentRef = useRef(null);
  const [selectionText, setSelectionText] = useState('');
  const [captureAt, setCaptureAt] = useState(null);
  // text whose prose relations are awaiting confirmation after a save
  const [proseFor, setProseFor] = useState(null);

  // AI decomposition (imported articles → atomic claims)
  const [decomposing, setDecomposing] = useState(false);
  const [decomposeNote, setDecomposeNote] = useState(null);

  // relation composer (rebuilt 2026-08-04 around the typed field — records/
  // RELATION_DRAWING_PREP.md). The relation dropdown, the ⇄ flip control and
  // the client-side corpus filter are gone: you pick the OTHER OBJECT from a
  // server-side search, then SAY the relation; the matcher resolves name and
  // direction together, so direction stopped being a control at all. (The old
  // flip note is preserved in spirit: crux_of runs question → claim while
  // bears_on runs claim → question, and the matcher now fixes both from the
  // endpoint types rather than trusting a toggle.)
  const [showCreateRelation, setShowCreateRelation] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null); // the whole object row, or null
  const [tagInput, setTagInput] = useState('');

  // compare tray
  const [compareIds, setCompareIds] = useState([]);

  const fetchAll = useCallback(async (withSpinner = false) => {
    try {
      if (withSpinner) setLoading(true);
      const [objRes, relRes, bdRes] = await Promise.all([
        apiFetch(`/api/objects/${id}`),
        apiFetch(`/api/relation-instances/${id}`),
        apiFetch(`/api/claims/${id}/breakdown`),
      ]);
      if (!objRes.ok) throw new Error('Object not found');
      setObject(await objRes.json());
      setRelations(await relRes.json());
      // 404 here just means the object sits outside the trust graph
      // (users, groups, tags) — the page still works without a trust surface.
      setBreakdown(bdRes.ok ? await bdRes.json() : null);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching object details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll(true);
    setCompareIds([id]);
  }, [id, fetchAll]);

  // the TRAIL (R7): every visited object page leaves a client-side breadcrumb
  // the pair page renders — the "where am I" locator half, and a pin source
  useEffect(() => {
    if (object?.id === id) recordVisit(object);
  }, [object, id]);

  const pins = usePins();
  const pinned = pins.some((p) => p.id === id);

  // Start a thread opening on this object (2026-08-06). Mints the thread PRIVATE
  // — no visible_to edge — and lands on its page, where the author stitches the
  // rest and publishes deliberately. Sharing is never a default someone has to
  // notice and undo.
  const startThread = useCallback(async () => {
    const title = window.prompt('Name this thread — it is what a shared link shows:');
    if (!title || !title.trim()) return;
    const res = await apiFetch('/api/threads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title.trim(), root_id: id }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      window.alert(body.error || 'Could not start a thread here');
      return;
    }
    const thread = await res.json();
    navigate(`/thread/${thread.id}`);
  }, [id, navigate]);

  // The whole-corpus fetch that used to sit here is GONE (2026-08-04): GET
  // /api/objects had no LIMIT and this page pulled every visible object on
  // every mount to feed a client-side substring filter behind the Details
  // fold. The relation form searches server-side now (below). /api/relations
  // stays — a small table, and the tag/extract handlers need ids from it.
  useEffect(() => {
    apiFetch('/api/relations')
      .then((r) => r.json())
      .then(setAllRelationTypes)
      .catch((err) => console.error('Error fetching relation types:', err));
  }, []);

  // The debounced, sequence-guarded object search that lived here moved to
  // ObjectPicker (extracted for the thread editor 2026-08-07, adopted back
  // here the same day) — one implementation of the two subtleties a second
  // hand-written copy would get wrong.

  // handleVote is gone with the flat list (2026-07-28): the tree passes
  // `onVoted` instead, so EdgeSentence casts through the shared `castVote`
  // (same toggle-to-withdraw semantics, read off the edge itself) and calls
  // back here to refetch — votes move trust scores, so the band refreshes too.

  // A hand-drawn edge is an ASSERTION, so it carries its author's +1 — the
  // convention TrustStance and proposeClass follow ("your +1 activates your
  // own assertion; unvoted edges are inert"). NOT a loosening of activation:
  // proposals (model output, decompose, retype) stay inert (DECISIONS.md
  // 2026-04-25). The `activateOwn` helper that used to live here moved INTO
  // RelationField (2026-08-04), which owns the whole draw commit — matching,
  // direction, minting an unknown type, the edge, and the author's +1 — for
  // both this page's form and the tree's candidate rows.
  // handleCreateRelation and handleCreateRelationType are gone with it.

  const handleAddTag = async (e) => {
    if (e.key !== 'Enter') return;
    const name = tagInput.trim();
    if (!name) return;
    const taggedRelationId = allRelationTypes.find((r) => r.name === 'tagged')?.id;
    if (!taggedRelationId) { setError('Tagged relation type not found'); return; }
    try {
      const searchRes = await apiFetch(`/api/objects?object_type=tag&q=${encodeURIComponent(name)}`);
      const candidates = await searchRes.json();
      let tagId = candidates.find((t) => t.content.toLowerCase() === name.toLowerCase())?.id;
      if (!tagId) {
        const createRes = await apiFetch('/api/objects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: name, object_type: 'tag' }),
        });
        if (!createRes.ok) throw new Error('Failed to create tag');
        tagId = (await createRes.json()).id;
      }
      const alreadyTagged = relations.some(
        (r) => r.relation_name === 'tagged' && r.source_id === id && r.target_id === tagId
      );
      if (!alreadyTagged) {
        const relRes = await apiFetch('/api/relation-instances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ relation_id: taggedRelationId, source_id: id, target_id: tagId, polarity: 1 }),
        });
        if (!relRes.ok) throw new Error('Failed to create tag relation');
        fetchAll();
      }
      setTagInput('');
    } catch (err) {
      setError('Failed to add tag');
      console.error('Error adding tag:', err);
    }
  };

  // The hand capture gesture. REPLACED 2026-08-07 (Slice A, open question #2):
  // this page used to mint its own way — a claim plus a derived_from edge, no
  // tag step, no anchor row, no author's +1, and it navigated you off the page
  // you were reading. That is the same gesture the Reader now offers, done
  // worse, and the ruling was that one minting process serves both surfaces.
  // handleExtractClaim is gone; CaptureClaim owns the commit.
  const handleContentMouseUp = () => {
    const sel = window.getSelection();
    const text = sel ? sel.toString().trim() : '';
    if (text.length >= 8 && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
      setSelectionText(text);
      // placeFromSelection is shared with the Reader so the two surfaces cannot
      // drift apart on the shape the owner picked (R-A, a positioned popover —
      // a bottom sheet on desktop is R-C, which lost).
      setCaptureAt(placeFromSelection(sel));
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const content = editContent.trim();
    if (!content) return;
    try {
      const response = await apiFetch(`/api/objects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) throw new Error('Failed to update object');
      setObject(await response.json());
      setIsEditing(false);
      setError(null);
      // Parse on EVERY save, not only at creation (open question #5, reversed
      // 2026-08-07): create-only meant typing `supports::[[x]]` into an object
      // you were editing did nothing forever, leaving the syntax sitting in the
      // text as a dead end. The chips are proposals; the dedup guard in
      // proseRelations.js is what makes re-saving safe.
      setProseFor(content);
    } catch (err) {
      setError('Failed to update object');
      console.error('Error updating object:', err);
    }
  };

  // File this tag under a broader one. Only reachable on a `tag` object page.
  //
  // The server takes a NAME and does the get-or-create, so typing a parent that
  // does not exist yet mints it — the same one-field gesture as tagging, for the
  // same reason (a client that searches then creates races itself).
  const handleAddTagParent = async (name) => {
    const res = await apiFetch(`/api/tags/${id}/parents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to file the tag');
    fetchAll();
  };

  const handleDeleteRelation = async (relationId) => {
    if (!(await wvConfirm('Delete this relation?', { confirmLabel: 'Delete' }))) return;
    try {
      const response = await apiFetch(`/api/relation-instances/${relationId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete relation');
      fetchAll();
    } catch (err) {
      setError('Failed to delete relation');
      console.error('Error deleting relation:', err);
    }
  };

  // Trust stance moved to the shared TrustStance component (vector-trust
  // phase 2): one affordance for actors AND claims, on every surface — the
  // 2026-07-26 reachability finding. Identified stakes (never a bare count):
  const stakedEdges = relations.filter(
    (r) =>
      r.relation_name === 'trusts' &&
      r.target_id === id &&
      !r.is_system &&
      r.up_votes + r.down_votes > 0
  );

  // Actor identity (2026-07-25 ruling): class is votable is_a edges to the
  // class concepts; a binding is a votable same_actor_as edge whose
  // ACTIVATED, net-positive state routes standing at read time. Both are
  // derived from the relations list — the edges themselves render as
  // votable sentences in Connections like everything else.
  const isActor = object && (object.object_type === 'user' || object.object_type === 'source');
  // A question is NOT truth-apt (2026-07-28): no truth value, so no lens
  // reading of it is meaningful. The backend already excludes it from lens
  // input (trust/graph.ts UNSCORED_OBJECT_TYPES), so `tension` arrives empty
  // — but this flag is DECLARED rather than inferred from that emptiness,
  // because "no band because it is a question" and "no band because nothing
  // has been voted on yet" are different states and must not render alike.
  const isQuestion = object && object.object_type === 'question';
  // The whole unscored family, not just questions (2026-08-08). Declared from
  // the TYPE for the reason stated above, and widened for a measured one: a
  // `viewset` handle already fell through to "No trust signal yet — nothing
  // around this object has been voted on", which invites a reader to go and vote
  // something into existence on an object that can never carry a score. Threads
  // and headings would have joined it the moment either reached this page.
  const unscoredKind = object && UNSCORED_KINDS[object.object_type] ? object.object_type : null;
  const classEdges = isActor
    ? relations.filter((r) => r.relation_name === 'is_a' && r.source_id === id && !r.is_system)
    : [];
  const activeBindingOut = isActor
    ? relations.find(
        (r) =>
          r.relation_name === 'same_actor_as' &&
          r.source_id === id &&
          !r.is_system &&
          r.up_votes + r.down_votes > 0 &&
          r.vote_score > 0
      )
    : null;
  const activeAliasesIn = isActor
    ? relations.filter(
        (r) =>
          r.relation_name === 'same_actor_as' &&
          r.target_id === id &&
          !r.is_system &&
          r.up_votes + r.down_votes > 0 &&
          r.vote_score > 0
      )
    : [];
  const ACTOR_CLASSES = ['person', 'outlet', 'institution', 'handle', 'agent', 'role'];
  const [classConcepts, setClassConcepts] = useState([]);
  useEffect(() => {
    if (!isActor) return;
    apiFetch('/api/concepts')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setClassConcepts(list.filter((c) => c.key.startsWith('class:'))))
      .catch(() => {});
  }, [isActor]);
  const proposeClass = async (key) => {
    const concept = classConcepts.find((c) => c.key === `class:${key}`);
    if (!concept) return;
    try {
      let isAId = allRelationTypes.find((r) => r.name === 'is_a')?.id;
      if (!isAId) {
        const made = await apiFetch('/api/relations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: 'is_a' }),
        });
        isAId = (await made.json()).id;
      }
      const res = await apiFetch('/api/relation-instances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relation_id: isAId, source_id: id, target_id: concept.tag_id, polarity: 1 }),
      });
      if (res.ok) {
        // your +1 activates your own classification (unvoted edges are inert)
        const edge = await res.json();
        await apiFetch('/api/votes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ relation_instance_id: edge.id, polarity: 1 }),
        });
        fetchAll();
      }
    } catch (err) {
      console.error('Class proposal failed:', err);
    }
  };

  // Decompose an imported article into atomic, source-attributed claims (edges
  // born inert). Auto-creates; the result is reviewed in-graph, not per-item.
  const handleDecompose = async () => {
    setDecomposing(true);
    setDecomposeNote(null);
    try {
      const res = await apiFetch(`/api/objects/${id}/decompose`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Decompose failed');
      setDecomposeNote(
        data.existing
          ? `Already decomposed (${data.claims} claims).`
          : `Created ${data.created} claims (${data.roots} root). Edges are inert until voted.`
      );
      fetchAll();
    } catch (err) {
      setDecomposeNote(err.message);
    } finally {
      setDecomposing(false);
    }
  };

  // handleQuickLink is GONE (2026-08-04): it hardcoded source_id: otherId,
  // target_id: id with no flip, so "this supports that" was unsayable from
  // the cheap path — the friction finding that started the ruling. The tree's
  // candidate rows now open a RelationField, which resolves direction from
  // the words typed.

  const toggleCompare = (objectId) => {
    setCompareIds((prev) =>
      prev.includes(objectId) ? prev.filter((x) => x !== objectId) : [...prev, objectId]
    );
  };

  if (loading) return <div style={{ padding: 'var(--wv-sp-8)' }}>Loading...</div>;
  if (error) return <div style={{ padding: 'var(--wv-sp-8)', color: 'var(--wv-neg)' }}>Error: {error}</div>;
  if (!object) return <div style={{ padding: 'var(--wv-sp-8)' }}>Object not found</div>;

  const tags = relations.filter((r) => r.relation_name === 'tagged' && r.source_id === id && !r.is_system);
  const systemEdges = relations.filter((r) => r.is_system);
  // Outgoing tags render as chips; system plumbing lives at L2. Everything
  // else goes to RelationTree, which owns grouping and ordering now.
  // a source page (claims extracted FROM it) has no argument of its own —
  // its walk door would open on a wall of article text; the Reader is its
  // argument surface (2026-07-25)
  const isSourcePage = relations.some((r) => r.relation_name === 'derived_from' && r.target_id === id);

  // How many edges could actually become the walk's first step, from data this
  // view already holds. Mirrors extractSpine's rule: a claim steps down
  // `supports` edges INTO it, a question down `bears_on`/`answers` into it, and
  // both require polarity +1 — a negated edge is not a step. Zero means the
  // Walk would open on its one-step degenerate branch, and the door says so.
  const walkableInto = relations.filter(
    (r) =>
      r.target_id === id &&
      r.polarity === 1 &&
      (isQuestion
        ? r.relation_name === 'bears_on' || r.relation_name === 'answers'
        : r.relation_name === 'supports')
  ).length;

  const claim = breakdown?.claim;
  const lenses = breakdown?.lenses || [];
  const tension = claim?.tension;
  // axes join the blend ONLY at viewer-raised weights (default 0 → identical
  // to plain recombine; vector-trust phase 2)
  const blend = claim ? recombineWithAxes(claim.scores, weights, breakdown?.axes, axisWeights) : 0;
  const adversarial = claim?.scores?.adversarial_consensus;
  const compareReady = compareIds.length >= 2 && compareIds.length <= 4;

  return (
    <div className="trust-page" style={{ paddingBottom: compareIds.length >= 2 ? '70px' : '20px' }}>
      <Nav />
      {/* Single centered column since the "Related" aside was deleted
          (2026-07-28). This was `.wv-split` at 1200px with a 780px main column
          beside the aside; removing the aside without this leaves the content
          stranded against the left edge with ~400px of dead gutter. */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '0 var(--wv-sp-8) 40px' }}>
        <div style={{ minWidth: 0 }}>
          <Link to="/" style={{ marginBottom: 'var(--wv-sp-7)', display: 'inline-block' }}>
            &larr; Home
          </Link>

          {/* ——— L0: the object itself ——— */}
          <div className="trust-section" style={{ borderColor: 'var(--wv-border-strong)' }}>
            {/* flexWrap: at phone widths the action links overflowed INTO the
                type label (space-between with no wrap); wrapping drops them to
                their own line instead */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--wv-sp-2) var(--wv-sp-5)', marginBottom: 'var(--wv-sp-2)' }}>
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <Explain concept="object">{TYPE_LABEL[object.object_type] || object.object_type}</Explain>
              </div>
              <div style={{ display: 'flex', gap: 'var(--wv-sp-2) var(--wv-sp-5)', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {object.object_type === 'text' && (
                  <Link to={`/read/${id}`} style={{ fontSize: 'var(--wv-fs-md)' }}>read →</Link>
                )}
                {/* the Walk is /argument/:id's default — name the door after it
                    (discoverability, 2026-07-25; the map lives inside as a toggle).
                    Source pages don't get it: their argument surface is the Reader.
                    Questions DO, as of 2026-08-01 (DECISIONS.md). They were cut on
                    2026-07-28 because the Walk's traversal covered REASONING edges
                    only, so the link opened an empty argument — true at the time,
                    but the fix was the traversal, not the link: crux_of/bears_on
                    now carry reachability on the same terms as qualifies, and a
                    question root walks through its candidate answers. */}
                {/* DEGENERACY-HONEST (Slice B, 2026-08-07). The door used to
                    promise a walk regardless, and on a claim nothing supports
                    it opened the one-step "no activated supporting chain leads
                    here yet" page — the link was the thing that lied, not the
                    page. `relations` is already fetched for this view, so the
                    test costs no request: a spine can only step down `supports`
                    into this claim, or `bears_on`/`answers` into a question
                    (walk.js), so with none of those there is nothing to walk. */}
                {!isSourcePage && (
                  walkableInto > 0 ? (
                    <Link to={`/argument/${id}`} style={{ fontSize: 'var(--wv-fs-md)' }}>
                      {isQuestion ? 'walk the question →' : 'walk the argument →'}{' '}
                      <span className="trust-muted">({walkableInto})</span>
                    </Link>
                  ) : (
                    <Link
                      to={`/argument/${id}`}
                      className="trust-muted"
                      style={{ fontSize: 'var(--wv-fs-md)' }}
                      title={
                        isQuestion
                          ? 'No candidate answer has been attached to this question yet'
                          : 'Nothing supports this claim yet, so the walk would be a single step'
                      }
                    >
                      {isQuestion ? 'no answers yet →' : 'nothing supports this yet →'}
                    </Link>
                  )
                )}
                {/* start a THREAD here (2026-08-06). Sits beside the Walk on
                    purpose: the two are the same kind of object under different
                    ordering rules — the Walk derives its chain from votes and
                    your lens weights, a thread is one you arrange by hand — and
                    the doors should read that way. Not offered on actor pages:
                    an actor is not a step in an argument. */}
                {user && !isSourcePage && object.object_type !== 'user' && (
                  <button onClick={startThread} style={{ fontSize: 'var(--wv-fs-md)' }}>
                    start a thread here →
                  </button>
                )}
                {/* ungated to source actors 2026-07-26: imports create 'source'
                    actors, and the two actor pages were one-way for exactly them */}
                {(object.object_type === 'user' || object.object_type === 'source') && (
                  <Link to={`/actor/${id}`} style={{ fontSize: 'var(--wv-fs-md)' }}>consistency timeline →</Link>
                )}
                {/* the WORKING SET pin (R2): collect objects while browsing,
                    relate them on the pair page (/triage). Client-side. */}
                {user && (
                  <button
                    onClick={() => togglePin(object)}
                    aria-pressed={pinned}
                    title={pinned ? 'In your working set — click to remove' : 'Pin to your working set, then relate pins on the pair page'}
                    style={{ fontSize: 'var(--wv-fs-sm)', fontWeight: pinned ? 700 : 400 }}
                  >
                    {pinned ? `✓ pinned · ${pins.length} in set` : 'pin'}
                  </button>
                )}
                {/* The set is otherwise invisible outside /triage, so the cue to go
                    relate has to live here, at the moment the second pin lands. */}
                {user && pinned && pins.length >= 2 && (
                  <Link to="/triage" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                    relate them →
                  </Link>
                )}
                {user && object.creator === user.id && !isEditing && (
                  <button onClick={() => { setEditContent(object.content); setIsEditing(true); }} style={{ fontSize: 'var(--wv-fs-sm)' }}>
                    Edit
                  </button>
                )}
              </div>
            </div>

            {isEditing ? (
              <form onSubmit={handleSaveEdit} style={{ marginBottom: 'var(--wv-sp-4)' }}>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={6}
                  style={{ width: '100%', padding: 'var(--wv-sp-3)', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 'var(--wv-sp-3)', marginTop: 'var(--wv-sp-3)' }}>
                  <button type="submit" disabled={!editContent.trim()}>Save</button>
                  <button type="button" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              // MARKDOWN IS READ-TIME INTERPRETATION (Slice C). Storage is
              // untouched: `objects.content` is the same plain text it always
              // was, and this is the only thing that changed about it.
              //
              // `whiteSpace: pre-wrap` is GONE here on purpose — it and block
              // markdown are two answers to the same question, and keeping both
              // double-spaces every rendered paragraph. Markdown.js owns the
              // line breaks now.
              //
              // The Reader's document body deliberately does NOT get this: its
              // `claim_anchors` offsets index the STORED text, and rendered
              // markdown puts different characters in different places, so an
              // anchored underline would drift off its sentence.
              <div
                ref={contentRef}
                onMouseUp={handleContentMouseUp}
                style={{ fontSize: 'var(--wv-fs-title)', fontWeight: 600, lineHeight: 'var(--wv-lh)', marginBottom: 'var(--wv-sp-4)' }}
              >
                <Markdown text={object.content} />
              </div>
            )}

            {/* WHO SAID IT (2026-08-14) — the shared byline, directly under the
                content, the same position and order the thread step uses.
                This page had NO attribution before: `authored_by` went into the
                connections list below, so a reader arriving from a published
                thread lost the one fact the thread had just told them.

                An ACTOR page is suppressed — a byline under Robert Hawkins
                reading "Robert Hawkins" is noise, and the timeline link beside
                the type label is already the right affordance there. */}
            {!isActor && <Byline attribution={object.attribution} />}

            {/* Confirm-before-draw for `name::[[target]]` written in the text
                (M-3). Appears after a save, never during composition. */}
            {proseFor && user && (
              <ProseRelationChips
                sourceId={id}
                text={proseFor}
                existing={relations}
                onDrawn={fetchAll}
              />
            )}

            {/* The shared capture modal (Slice A) — same component, same door
                and same placement rule as the Reader. */}
            {selectionText && !isEditing && user && (
              <CaptureClaim
                sourceId={id}
                quote={selectionText}
                sheet={captureAt?.sheet}
                position={captureAt?.position}
                modelAccess={object?.model_access}
                // Deliberately does NOT clear selectionText: this component is
                // mounted on `selectionText`, so clearing it here unmounts the
                // modal at the moment it succeeds and the confirmation — minted,
                // anchored or not, pinned, relate-it-on-/triage — never renders.
                // Caught in the drive, 2026-08-07. Closing is the user's move.
                // The document selection clears on SUCCESS only (2026-08-11):
                // cancel/click-away leaves it alone, same ruling as the Reader —
                // wiping the range made select-to-copy impossible.
                onDone={() => {
                  window.getSelection()?.removeAllRanges();
                  fetchAll();
                }}
                onClose={() => setSelectionText('')}
              />
            )}
            {selectionText && !isEditing && !user && (
              <div style={{ marginBottom: 'var(--wv-sp-4)', padding: 'var(--wv-sp-4)', border: '1px dashed var(--wv-warn)', backgroundColor: 'color-mix(in srgb, var(--wv-warn) 10%, transparent)', fontSize: 'var(--wv-fs-md)' }}>
                <span style={{ color: 'var(--wv-muted)' }}>Log in to mint a claim from a selection.</span>
                <button onClick={() => setSelectionText('')} style={{ marginLeft: 'var(--wv-sp-3)' }}>Dismiss</button>
              </div>
            )}

            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--wv-sp-2)', marginBottom: 'var(--wv-sp-5)' }}>
                {/* several tagged edges can carry the same tag (e.g. repeated
                    corpus imports) — one chip per tag content */}
                {[...new Map(tags.map((rel) => [rel.target_content, rel])).values()].map((rel) => (
                  <Link key={rel.id} to={`/trust/${rel.target_id}`} className="lens-chip" style={{ fontSize: 'var(--wv-fs-sm)', textDecoration: 'none', color: 'inherit' }}>
                    {rel.target_content}
                  </Link>
                ))}
              </div>
            )}

            {/* Actor identity (2026-07-25 ruling, built 2026-07-26): class
                proposals + accepted-binding annotation. The edges themselves
                are votable sentences in Connections; this row is the readout
                and the one-click proposal door. */}
            {isActor && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)', flexWrap: 'wrap', margin: 'var(--wv-sp-3) 0 var(--wv-sp-0)' }}>
                <Explain
                  title="Actor class"
                  text="What KIND of actor this is (person / outlet / institution / handle / agent / role) — a votable is_a connection to the class concept, read at query time. A misclassification is an edge to out-vote, not a migration. Until a class edge is voted, the class is simply unknown."
                >
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>class:</span>
                </Explain>
                {classEdges.length > 0 ? (
                  classEdges.map((r) => (
                    <span key={r.id} className="wv-chip" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                      {r.target_content}
                      {r.up_votes + r.down_votes === 0 ? ' (proposed)' : r.vote_score <= 0 ? ' (rejected)' : ''}
                    </span>
                  ))
                ) : (
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>unknown</span>
                )}
                {user && classConcepts.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => e.target.value && proposeClass(e.target.value)}
                    style={{ fontSize: 'var(--wv-fs-sm)' }}
                    title="Propose a class — mints a votable is_a connection with your activating vote"
                  >
                    <option value="">classify…</option>
                    {ACTOR_CLASSES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {activeBindingOut && (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', margin: 'var(--wv-sp-0) 0' }}>
                <Explain
                  title="Identity binding"
                  text="An accepted same_actor_as binding ROUTES this actor's standing — attribution, trust edges, votes — to its owner at read time. Nothing is merged: withdraw the vote on the binding edge (see Connections) and the routing dissolves. That reversibility is why routing beat merging."
                >
                  ↳ standing routes to <strong>{activeBindingOut.target_content}</strong> (accepted binding)
                </Explain>
              </div>
            )}
            {activeAliasesIn.length > 0 && (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', margin: 'var(--wv-sp-0) 0' }}>
                also known as: {activeAliasesIn.map((r) => r.source_content).join(' · ')}{' '}
                (accepted bindings — their standing accrues here)
              </div>
            )}

            {/* Trust stance — actors AND claims (vector-trust phase 2): the
                shared one-click affordance. For claims the stake reads on the
                SOCIAL axis, beside the evidence, never inside the band. */}
            {user &&
              (object.object_type === 'user' ||
                object.object_type === 'source' ||
                object.object_type === 'text') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)', flexWrap: 'wrap', margin: 'var(--wv-sp-4) 0 var(--wv-sp-1)' }}>
                  <TrustStance
                    targetId={id}
                    kind={object.object_type === 'text' ? 'claim' : 'actor'}
                    onChanged={fetchAll}
                  />
                  {stakedEdges.length > 0 && (
                    <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                      {/* identified stances, never a bare count (DECISIONS.md 2026-07-26) */}
                      staked:{' '}
                      {stakedEdges
                        .map((r) => `${r.source_content || 'someone'} ${r.polarity === 1 ? 'trusts' : 'distrusts'}`)
                        .join(' · ')}
                    </span>
                  )}
                </div>
              )}

            {/* Standalone axes (vector-trust phase 2): per-relation tallies,
                edge polarity as sign. Band-inert until a drawer slider rises. */}
            {breakdown?.axes?.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-2)', flexWrap: 'wrap', margin: 'var(--wv-sp-2) 0 var(--wv-sp-1)' }}>
                <Explain
                  title="Axes"
                  text="Every relation type is its own axis (vector trust, 2026-07-26): activated non-epistemic edges pointing at this object, tallied per relation name, with edge polarity as the sign. An axis never moves the trust band unless you raise its weight in the Viewpoint drawer — supports/contradicts stay the band itself."
                >
                  <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>axes:</span>
                </Explain>
                {breakdown.axes.map((a) => (
                  <span key={a.axis} className="wv-chip" style={{ fontSize: 'var(--wv-fs-sm)' }} title={`score ${fmtScore(a.score)}`}>
                    {a.axis} {a.net > 0 ? '+' : ''}{a.net} · {a.count === 1 ? '1 stake' : `${a.count} stakes`}
                    {a.aka && (
                      <span className="trust-muted" style={{ marginLeft: 'var(--wv-sp-1)' }}>
                        {/* convergence routing (phase 3): identity stays the senior name; usage shows as aka */}
                        aka {a.aka.join(', ')}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* A question gets NO band and no readout — absence, not a zero.
                One muted line; the ⓘ carries the whole explanation (standing UI
                principle 2026-07-28: text-minimal, explain mode does the heavy
                lifting). This branch comes FIRST and the three-state readout
                below is gated on it, so a question can never fall through into
                the claim rendering. */}
            {unscoredKind && (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-md)', fontStyle: 'italic', margin: 'var(--wv-sp-5) 0 var(--wv-sp-1)' }}>
                <Explain concept={UNSCORED_KINDS[unscoredKind].concept}>
                  {UNSCORED_KINDS[unscoredKind].line}
                </Explain>
              </div>
            )}

            {/* Trust readout — three-state: absence / consensus / contested */}
            {!unscoredKind && tension && tension.state === 'no_signal' && (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-md)', fontStyle: 'italic', margin: 'var(--wv-sp-5) 0 var(--wv-sp-1)' }}>
                <Explain concept="no-signal">
                  No trust signal yet — nothing around this object has been voted on.
                </Explain>
              </div>
            )}
            {!unscoredKind && tension && tension.state !== 'no_signal' && (
              <div style={{ margin: 'var(--wv-sp-5) 0 var(--wv-sp-1)' }}>
                <Explain concept="trust-band">
                  <TrustBandDrag scores={claim.scores} lenses={lenses} blend={blend} width={300} />
                </Explain>
                {/* the readout row — the Instrument's discipline: every figure a
                    figure (BLEND_REDESIGN_SPEC.md); the sans caption below keeps
                    the Field's plain-language reading of the same numbers */}
                <div
                  className="wv-data trust-muted"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--wv-sp-0) var(--wv-sp-6)', marginTop: 'var(--wv-sp-3)', fontSize: 'var(--wv-fs-xs)' }}
                >
                  <span>blend <strong style={{ color: scoreColor(blend) }}>{fmtScore(blend)}</strong></span>
                  <span>spread <strong style={{ color: 'var(--wv-text)' }}>{tension.spread.toFixed(2)}</strong></span>
                  {typeof adversarial === 'number' && (
                    <span>adversarial <strong style={{ color: scoreColor(adversarial) }}>{fmtScore(adversarial)}</strong></span>
                  )}
                  {typeof claim.scores?.source_trust === 'number' && (
                    <span>source_trust <strong style={{ color: scoreColor(claim.scores.source_trust) }}>{fmtScore(claim.scores.source_trust)}</strong></span>
                  )}
                  {tension.state === 'contested' && (
                    <span style={{ color: 'var(--wv-warn)', fontWeight: 700 }}>⚡ contested</span>
                  )}
                  <span title="Drag the band to borrow nearby lenses' view">drag to explore</span>
                </div>
                <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-md)', marginTop: 'var(--wv-sp-2)' }}>
                  Your lenses lean{' '}
                  <strong style={{ color: 'var(--wv-text)' }}>
                    {fmtScore(blend)} {blend >= 0.05 ? 'toward holding' : blend <= -0.05 ? 'against holding' : 'near neutral'}
                  </strong>
                  {tension.state === 'contested' ? ' — but they disagree widely.' : '.'}
                </div>
                {tension.top && (
                  <div style={{ marginTop: 'var(--wv-sp-3)', fontSize: 'var(--wv-fs-ui)' }}>
                    <Explain concept="tension">
                      <span style={{ borderLeft: '3px solid var(--wv-warn)', paddingLeft: 'var(--wv-sp-4)' }}>
                        {tension.top.sentence}
                      </span>
                    </Explain>
                    {!isSourcePage && (
                      <>
                        {' '}
                        <Link to={`/argument/${id}`} style={{ fontSize: 'var(--wv-fs-md)', whiteSpace: 'nowrap' }}>
                          walk this disagreement →
                        </Link>
                      </>
                    )}
                  </div>
                )}
                {tension.tensions.length > 1 && (
                  <div className="trust-muted" style={{ marginTop: 'var(--wv-sp-2)', fontSize: 'var(--wv-fs-md)' }}>
                    {tension.tensions.slice(1).map((t) => (
                      <div key={t.id} style={{ marginTop: 'var(--wv-sp-1)' }}>{t.sentence}</div>
                    ))}
                  </div>
                )}
                {adversarial === 0 && (
                  <div className="trust-muted" style={{ marginTop: 'var(--wv-sp-2)', fontSize: 'var(--wv-fs-sm)' }}>
                    Hasn’t survived cross-group division yet — no vote on its evidence spans separated trust clusters.
                  </div>
                )}
              </div>
            )}

            {breakdown?.source && (
              <div style={{ marginTop: 'var(--wv-sp-5)', fontSize: 'var(--wv-fs-md)', padding: 'var(--wv-sp-4) var(--wv-sp-5)', backgroundColor: 'var(--wv-surface2)', borderRadius: '8px' }}>
                <span className="trust-muted">extracted from: </span>
                <Link to={`/object/${breakdown.source.id}`}>{snip(breakdown.source.content, 130)}</Link>
                <span className="trust-muted"> · </span>
                <Link to={`/read/${breakdown.source.id}?d=2`}>read in context →</Link>
              </div>
            )}
          </div>

          {/* ——— L0: connections as a tree of sentences ———
              Replaced the flat capped-at-8 list 2026-07-28 (owner design
              session; TODO "Question objects and the relation tree"). The tree
              is a REPLACEMENT, not an addition — that was the test its own
              guardrail set, so if it ever ships alongside a second listing of
              the same edges, it has failed. The per-visit frozen edge ordering
              the flat list needed is gone with it: the tree groups by relation,
              so a vote no longer moves a row out from under the cursor. */}
          {/* A TAG's own page gets one extra control: where it sits in the
              taxonomy. The hierarchy relation has existed since the seed and
              already RENDERS in the tree below (is_subcategory_of is not hidden,
              unlike `tagged`) — what was missing was a way to write one without
              hand-copying a UUID into the generic relation form. Rollup
              filtering (migration 018) is what made it worth a door.

              The copy carries the direction, because the gesture is ambiguous
              otherwise: TagField normally means "attach this to that", and here
              it has to read "this sits under that". The stored edge is always
              child → parent, and this page is always the child. */}
          {object.object_type === 'tag' && user && (
            <div style={{ marginTop: 'var(--wv-sp-6)' }}>
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginBottom: 'var(--wv-sp-2)' }}>
                sits under
              </div>
              <TagField
                placeholder="file it under a broader tag — type a name, Enter to file…"
                tags={relations
                  .filter(
                    (r) =>
                      r.relation_name === 'is_subcategory_of' &&
                      r.source_id === id &&
                      r.polarity === 1
                  )
                  .map((r) => ({ key: r.id, name: r.target_content }))}
                onAdd={handleAddTagParent}
                onRemove={(t) => handleDeleteRelation(t.key)}
              />
            </div>
          )}

          <div style={{ marginTop: 'var(--wv-sp-8)' }}>
            <RelationTree
              rootId={id}
              rootIsQuestion={isQuestion}
              rootType={object.object_type}
              edges={relations}
              user={user}
              onChanged={fetchAll}
              candidates={breakdown?.similar}
              siblingCandidates={breakdown?.siblingCandidates}
              lenses={lenses}
              compareIds={compareIds}
              onToggleCompare={toggleCompare}
              rowActions={(rel, depth) =>
                user && depth === 0 ? (
                  <button onClick={() => handleDeleteRelation(rel.id)} style={{ fontSize: 'var(--wv-fs-xs)' }}>
                    delete
                  </button>
                ) : null
              }
            />
          </div>

          {/* ——— L1: details ——— */}
          <Fold label="Details" open={showDetails} onToggle={() => setShowDetails(!showDetails)}>
            {claim && (
              <div className="trust-section" style={{ marginBottom: 'var(--wv-sp-6)' }}>
                <LensChips lenses={lenses} scores={claim.scores} />
              </div>
            )}

            {/* Decompose imported articles into atomic claims. Only ai_visible
                text/link objects can be sent to the model (sealed → 403). */}
            {user && (object.object_type === 'text' || object.object_type === 'link') && object.model_access !== 'sealed' && (
              <div className="trust-section" style={{ marginBottom: 'var(--wv-sp-6)' }}>
                <div style={{ display: 'flex', gap: 'var(--wv-sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={handleDecompose} disabled={decomposing}>
                    {decomposing ? 'Decomposing…' : '✦ Decompose into claims'}
                  </button>
                  <Explain
                    title="Decompose into claims"
                    text="An AI reads this article and extracts its atomic claims — root assertions, supporting evidence, counter-positions — as new claim objects, attributed to the article's source. Connections are born inert (they count for nothing until a human votes). Faithful to the text; review the result in the graph."
                  >
                    <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                      AI extracts source-attributed claims; inert until voted
                    </span>
                  </Explain>
                </div>
                {decomposeNote && (
                  <div style={{ fontSize: 'var(--wv-fs-sm)', color: 'var(--wv-pos)', marginTop: 'var(--wv-sp-2)' }}>{decomposeNote}</div>
                )}
              </div>
            )}

            {user && (
              <div style={{ marginBottom: 'var(--wv-sp-6)', display: 'flex', gap: 'var(--wv-sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    setShowCreateRelation(!showCreateRelation);
                    setSelectedTarget(null);
                  }}
                >
                  {showCreateRelation ? 'Cancel' : '+ Connect'}
                </button>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder="add a tag (enter)"
                  style={{ padding: 'var(--wv-sp-2) var(--wv-sp-4)', width: '180px' }}
                />
              </div>
            )}

            {showCreateRelation && (
              <div
                className="wv-panel"
                style={{ marginBottom: 'var(--wv-sp-8)', backgroundColor: 'var(--wv-surface2)' }}
              >
                <h3 style={{ marginTop: 0 }}>Connect to another object</h3>
                <div style={{ marginBottom: 'var(--wv-sp-6)' }}>
                  <label style={{ display: 'block', marginBottom: 'var(--wv-sp-1)', fontWeight: 'bold' }}>
                    Find the other object:
                  </label>
                  {/* Picking again replaces the selection; the picker stays
                      mounted so the target can be changed before drawing.
                      ObjectPicker's machinery filter applies here too — a
                      relation handle or thread is browsable, not drawable. */}
                  <ObjectPicker
                    onPick={(obj) => setSelectedTarget(obj)}
                    excludeIds={[id]}
                    placeholder="Search by content…"
                    limit={30}
                  />
                </div>

                {/* The typed field replaces the relation dropdown AND the ⇄
                    flip (2026-08-04): say the relation — "supports", "is
                    refuted by", "answers" — and the matcher resolves name and
                    direction together. An inverse phrasing swaps the endpoints
                    visibly in the match row before anything is committed. */}
                {selectedTarget && (
                  <div>
                    <label style={{ display: 'block', marginBottom: 'var(--wv-sp-1)', fontWeight: 'bold' }}>
                      Say the connection — “selected” is “{snip(selectedTarget.content, 60)}”:
                    </label>
                    <RelationField
                      a={{ id: selectedTarget.id, type: selectedTarget.object_type }}
                      b={{ id, type: object.object_type }}
                      aLabel="selected"
                      bLabel="this"
                      user={user}
                      autoFocus
                      onDrawn={() => {
                        fetchAll();
                        setShowCreateRelation(false);
                        setSelectedTarget(null);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* The L1 "edges grouped by relation" management list is GONE
                (2026-07-28): the tree above already groups by relation, so
                keeping it would have been a second listing of the same edges on
                one page — the exact failure the tree's guardrail names. Its one
                unique capability, delete, moved INTO the tree at depth 0 via
                rowActions, so nothing was lost with it. */}

            {/* BOTH NeighborRow lists are GONE (2026-07-28), and the tree
                absorbed what each was for:

                "From the same source" was a second listing of objects the tree
                already reaches — expand the derived_from row and you land on the
                page, whose other extracted claims are its children. A shortcut
                for a one-click path is the same guardrail failure the flat L0
                list was.

                "Similar, not yet linked" was NOT redundant — those objects have
                no edge, so no tree of edges can reach them, and it was the only
                door for pulling one into the graph. It moved rather than died:
                it is the tree's "not yet connected" group, where picking a verb
                draws the edge. The trust band and lens chips those cards carried
                moved too, as the per-row trust disclosure.

                Net: three surfaces became one. */}
          </Fold>

          {/* ——— L2: machinery ——— */}
          <Fold label="Machinery" open={showMachinery} onToggle={() => setShowMachinery(!showMachinery)}>
            <div className="trust-section" style={{ fontSize: 'var(--wv-fs-md)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: 'var(--wv-sp-2) var(--wv-sp-7)' }}>
                <span className="trust-muted">type</span>
                <span>{object.object_type}</span>
                <span className="trust-muted">created by</span>
                <span>{claim?.creator_username || object.creator || 'system'}</span>
                <span className="trust-muted">added</span>
                <span>{new Date(object.created_at).toLocaleString()}</span>
                {object.asserted_at && (
                  <>
                    <span className="trust-muted">stated in the world</span>
                    <span>{new Date(object.asserted_at).toLocaleString()}</span>
                  </>
                )}
                <span className="trust-muted">model visibility</span>
                <span>
                  {object.model_access === 'public_ai'
                    ? 'any model — may be sent to a third-party model through the egress gate'
                    : object.model_access === 'private_ai'
                    ? 'local only — may be read by a model this instance runs, and never leaves it'
                    : 'sealed — no model may read it, including a local one'}
                </span>
                {object.arrival && object.arrival !== 'live' && (
                  <>
                    <span className="trust-muted">arrival</span>
                    <span>
                      <Explain concept="arrival-path">
                        {object.arrival === 'model_relay'
                          ? `relayed${object.arrival_via ? ` by ${object.arrival_via}` : ''} — the text was passed along by a model, not fetched from the source`
                          : `via archive snapshot${object.arrival_via ? ` (${object.arrival_via})` : ''}`}
                      </Explain>
                    </span>
                  </>
                )}
                <span className="trust-muted">id</span>
                <span style={{ fontFamily: 'monospace', fontSize: 'var(--wv-fs-sm)' }}>{object.id}</span>
              </div>
              <div style={{ marginTop: 'var(--wv-sp-5)' }}>
                <Link to="/trust/inspect" style={{ fontSize: 'var(--wv-fs-md)' }}>trust engine inspector →</Link>
              </div>
            </div>

            {/* Widen-only re-share (2026-08-13): the one post-mint mover of the
                human axis. The door only ever ADDS visibility — narrowing is
                inexpressible — so the affordance says "wider", not "change". */}
            {user && object.creator === user.id && (
              <ShareWider objectId={id} />
            )}

            {systemEdges.length > 0 && (
              <div style={{ marginTop: 'var(--wv-sp-6)' }}>
                <h3 style={{ marginBottom: 'var(--wv-sp-3)', fontSize: 'var(--wv-fs-ui)' }}>
                  System edges <span className="trust-muted" style={{ fontWeight: 'normal' }}>({systemEdges.length})</span>
                </h3>
                <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginTop: 0 }}>
                  Created automatically (authorship, visibility). Read-only here.
                </p>
                {systemEdges.map((rel) => (
                  <EdgeSentence key={rel.id} edge={rel} focusId={id} user={null} />
                ))}
              </div>
            )}

            {/* delete door — own objects only. Soft delete (deleted_at):
                history survives for trust analysis; hard deletes are
                structurally blocked. Two-step confirm, no modal. */}
            {user && object.creator === user.id && (
              <div style={{ marginTop: 'var(--wv-sp-8)', paddingTop: 'var(--wv-sp-5)', borderTop: '1px solid var(--wv-border)' }}>
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    style={{ fontSize: 'var(--wv-fs-md)', color: 'var(--wv-neg)', background: 'none' }}
                  >
                    Delete this object…
                  </button>
                ) : (
                  <div style={{ fontSize: 'var(--wv-fs-md)' }}>
                    <span className="trust-muted">
                      Soft delete — the row is kept for history and its edges leave every feed, lens, and search. Votes others cast are not touched.
                    </span>
                    <div style={{ marginTop: 'var(--wv-sp-3)', display: 'flex', gap: 'var(--wv-sp-4)' }}>
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiFetch(`/api/objects/${id}`, { method: 'DELETE' });
                            if (!res.ok) {
                              const data = await res.json().catch(() => ({}));
                              setDeleteError(data.error || 'Delete failed');
                              return;
                            }
                            navigate('/?view=everything&sort=new');
                          } catch {
                            setDeleteError('Delete failed — are you still signed in?');
                          }
                        }}
                        style={{ fontSize: 'var(--wv-fs-md)', color: 'var(--wv-bg)', background: 'var(--wv-neg)', border: '1px solid var(--wv-neg)' }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => { setConfirmDelete(false); setDeleteError(null); }}
                        style={{ fontSize: 'var(--wv-fs-md)', background: 'none', color: 'var(--wv-text)' }}
                      >
                        Keep it
                      </button>
                    </div>
                    {deleteError && (
                      <div style={{ marginTop: 'var(--wv-sp-2)', color: 'var(--wv-neg)' }}>{deleteError}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Fold>
        </div>
        {/* The "Related" aside is GONE (2026-07-28, owner call). It was the
            THIRD listing of the same material and the one that made the tree
            fail its own guardrail: of its three card origins, `relation` was a
            second rendering of the very edges the tree shows (with its own vote
            controls), and `similar` was the "not yet connected" group under
            another name. Only `domain` ("Also in: <tag>") was unique, and that
            is one click away on the tag pages and in Browse.
            Its min-trust slider and relation filter went with it — they were
            controls for a list that no longer exists.
            The backend endpoint (`GET /api/objects/:id/context`) still stands
            and now has no caller; retiring it is filed in TODO rather than done
            here, because it owns tests and is one of the `findSimilar` callers
            under review. */}
      </div>

      {/* compare tray */}
      {compareIds.length >= 2 && (
        <div
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            backgroundColor: 'var(--wv-text)', color: 'var(--wv-bg)', padding: 'var(--wv-sp-4) var(--wv-sp-8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--wv-sp-7)', zIndex: 50,
          }}
        >
          <span style={{ fontSize: 'var(--wv-fs-ui)' }}>{compareIds.length} objects selected</span>
          <button
            onClick={() => compareReady && navigate(`/compare?ids=${compareIds.join(',')}`)}
            disabled={!compareReady}
            style={{ padding: 'var(--wv-sp-2) var(--wv-sp-7)', fontSize: 'var(--wv-fs-ui)', cursor: compareReady ? 'pointer' : 'not-allowed' }}
          >
            Compare →
          </button>
          {compareIds.length > 4 && <span style={{ fontSize: 'var(--wv-fs-sm)', color: 'var(--wv-neg)' }}>max 4</span>}
          <button onClick={() => setCompareIds([id])} style={{ fontSize: 'var(--wv-fs-sm)' }}>clear</button>
        </div>
      )}
    </div>
  );
}

export default ObjectDetail;
